/**
 * Constants of the budgeting module. Keys and codes mirror the API package
 * (`W4msolutions\Budgeting`): permission keys are the ones of
 * `PermissionCatalog`, states the ones of `QuoteVersion`.
 */

/** Permission keys of the module (API `w4m_budget_permissions.key`). */
export const PERMISSIONS = {
  CREATE: "create",
  EDIT: "edit",
  SUBMIT: "submit",
  APPROVE: "approve",
  SEE_ALL: "see_all",
  SEE_COSTS: "see_costs",
  EDIT_MARGINS: "edit_margins",
  EDIT_CONDITIONS: "edit_conditions",
  MANAGE_CLIENTS: "manage_clients",
  MANAGE_SETTINGS: "manage_settings",
  MANAGE_PERMISSIONS: "manage_permissions",
  MANAGE_INBOX: "manage_inbox",
};

/** Any of these opens the quotes list and the editor. */
export const QUOTE_ACCESS = [
  PERMISSIONS.CREATE,
  PERMISSIONS.EDIT,
  PERMISSIONS.SEE_ALL,
  PERMISSIONS.SUBMIT,
  PERMISSIONS.APPROVE,
];

/** Either of these opens the commercial agreements. */
export const AGREEMENTS_ACCESS = [PERMISSIONS.MANAGE_CLIENTS, PERMISSIONS.MANAGE_SETTINGS];

/** Any of these opens the inbox («Entradas»); manage_inbox sees and handles every entry. */
export const INBOX_ACCESS = [PERMISSIONS.CREATE, PERMISSIONS.EDIT, PERMISSIONS.SEE_ALL, PERMISSIONS.MANAGE_INBOX];

/**
 * The «Orçamentos» hub (`/quotes?dom=`): one catalogue for the sidebar item, the route guard and the tabs, so the
 * three never disagree on who sees what. Each tab is the old page, untouched; `path` is that page's old route, now a
 * redirect into the hub; `permissions` is all-of, `permissionsAny` any-of (as `meta.budgeting`).
 */
export const HUB_ITEMS = [
  {
    value: "orcamentos",
    label: "Orçamentos",
    icon: "pi pi-fw pi-list",
    caption: "Orçamentos por cliente, local e serviço, e os grupos",
    permissionsAny: QUOTE_ACCESS,
  },
  {
    value: "entradas",
    label: "Entradas",
    icon: "pi pi-fw pi-inbox",
    caption: "Surveys e propostas que chegaram para orçamentar",
    permissionsAny: INBOX_ACCESS,
    path: "/quotes/inbox",
  },
  {
    value: "acordos",
    label: "Acordos Comerciais",
    icon: "pi pi-fw pi-percentage",
    caption: "Preços e descontos acordados, os clientes e os parceiros a que se aplicam",
    permissionsAny: AGREEMENTS_ACCESS,
    path: "/quotes/agreements",
  },
  {
    value: "permissoes",
    label: "Permissões",
    icon: "pi pi-fw pi-lock",
    caption: "Papéis, utilizadores, acesso por serviço e parceiros",
    permissions: [PERMISSIONS.MANAGE_PERMISSIONS],
    path: "/quotes/access",
    // the page's own tabs, in `?sub=` (the hub has `?dom=`)
    nested: true,
  },
  {
    value: "administracao",
    label: "Painel de Administração",
    icon: "pi pi-fw pi-cog",
    caption: "Serviços, tabelas de produtos, opções, filiais e assistente",
    permissions: [PERMISSIONS.MANAGE_SETTINGS],
    path: "/quotes/settings",
    nested: true,
  },
];

/** The summary the hub opens with, for whoever sees any of its tabs. */
export const HUB_SUMMARY = "resumo";

/**
 * What opens in the hub's side panel (`?panel=<kind>` or `?panel=<kind>:<id>`), over whatever tab is open.
 * `home` is the tab a link from outside the hub lays under the panel; `extras` are the query parameters the
 * panel's page reads and that leave the URL with it.
 */
export const PANELS = {
  new: { title: "Novo orçamento", permission: PERMISSIONS.CREATE, extras: ["client_no", "entity_stamp"] },
  table: {
    title: "Tabela de produtos",
    permission: PERMISSIONS.MANAGE_SETTINGS,
    withId: true,
    home: { dom: "administracao", sub: "tabelas" },
  },
  blocks: {
    title: "Perguntas e linhas",
    permission: PERMISSIONS.MANAGE_SETTINGS,
    withId: true,
    extras: ["sep"],
    home: { dom: "administracao", sub: "servicos" },
  },
  conditions: {
    title: "Condições e parâmetros",
    permission: PERMISSIONS.MANAGE_SETTINGS,
    withId: true,
    home: { dom: "administracao", sub: "servicos" },
  },
};

/** Any of these opens the hub (the union of its tabs). */
export const HUB_ACCESS = [
  ...new Set([
    ...QUOTE_ACCESS,
    ...INBOX_ACCESS,
    ...AGREEMENTS_ACCESS,
    PERMISSIONS.MANAGE_PERMISSIONS,
    PERMISSIONS.MANAGE_SETTINGS,
  ]),
];

/** States of an inbox entry (API `SurveyInbox::STATES`), derived from its quotes. */
export const INBOX_STATES = [
  { value: "unclassified", label: "Por classificar", severity: "warning", icon: "pi pi-question-circle" },
  { value: "review", label: "Por rever", severity: "info", icon: "pi pi-comments" },
  { value: "in_progress", label: "Em curso", severity: "info", icon: "pi pi-pencil" },
  { value: "done", label: "Concluída", severity: "success", icon: "pi pi-check" },
  { value: "discarded", label: "Descartada", severity: "secondary", icon: "pi pi-ban" },
];

/** The chips of the inbox: every state plus «todas» (the API's `filter`). */
export const INBOX_FILTERS = [{ value: "all", label: "Todas", icon: "pi pi-inbox" }, ...INBOX_STATES];

export const INBOX_ORIGINS = [
  { value: "pat", label: "Survey (PAT)" },
  { value: "proposal", label: "Proposta" },
  { value: "api", label: "Sistema externo (API)" },
];

export const DISCARD_REASONS = [
  { value: "duplicate", label: "Duplicada" },
  { value: "no_budget", label: "Não é para orçamentar" },
  { value: "other", label: "Outro motivo" },
];

/** How the assistant's answers are labelled (API `confidence`). */
export const ASSISTANT_CONFIDENCE = [
  { value: "high", label: "Alta", severity: "success" },
  { value: "medium", label: "Média", severity: "warning" },
  { value: "low", label: "Baixa", severity: "danger" },
];

/** The editor polls the assistant while a request is pending, up to the API's timeout. */
export const ASSISTANT_POLL_MS = 2500;
export const ASSISTANT_MAX_WAIT_MS = 240000;

export const QUOTE_STATES = [
  { value: "draft", label: "Rascunho", severity: "info" },
  { value: "submitted", label: "Submetido", severity: "warning" },
  { value: "approved", label: "Aprovado", severity: "success" },
  { value: "rejected", label: "Rejeitado", severity: "danger" },
  { value: "closed", label: "Concluído", severity: "success" },
];

/** The commercial state of a concluded version (API `version.commercial.code`). */
export const COMMERCIAL_STATES = [
  { value: "concluded", label: "A aguardar cliente", severity: "success" },
  { value: "sold", label: "Vendido", severity: "success" },
  { value: "not_sold", label: "Não vendido", severity: "danger" },
  { value: "expired", label: "Expirado", severity: "warning" },
];

/** The chips of the list: a version's state, its commercial code, or the price alert (API `tokens`). */
export const LIST_CHIPS = [
  { value: "draft", label: "Rascunho", severity: "info" },
  { value: "submitted", label: "Por aprovar", severity: "warning" },
  { value: "concluded", label: "A aguardar cliente", severity: "success" },
  { value: "sold", label: "Vendidos", severity: "success" },
  { value: "not_sold", label: "Não vendidos", severity: "danger" },
  { value: "expired", label: "Expirados", severity: "warning" },
  { value: "price_alert", label: "Alerta de preços", severity: "warning" },
  { value: "rejected", label: "Rejeitados", severity: "danger" },
];

/** SLA and support terms (API `SlaTerms`): codes stored, labels shown. */
export const SLA_FIELDS = [
  {
    key: "sla_local",
    label: "SLA Local",
    options: [
      { value: "4h", label: "4 horas" },
      { value: "8h", label: "8 horas" },
      { value: "24h", label: "24 horas" },
      { value: "48h", label: "48 horas" },
      { value: "72h", label: "72 horas" },
      { value: "na", label: "N/A" },
    ],
  },
  {
    key: "sla_remote",
    label: "SLA Remoto",
    options: [
      { value: "4h", label: "4 horas" },
      { value: "8h", label: "8 horas" },
      { value: "24h", label: "24 horas" },
      { value: "48h", label: "48 horas" },
      { value: "72h", label: "72 horas" },
      { value: "na", label: "N/A" },
    ],
  },
  {
    key: "support_type",
    label: "Tipo de suporte",
    options: [
      { value: "monitorizacao", label: "Monitorização" },
      { value: "remoto", label: "Remoto" },
      { value: "local", label: "Local" },
      { value: "na", label: "N/A" },
    ],
  },
  {
    key: "support_period",
    label: "Período de suporte",
    options: [
      { value: "24x7", label: "24x7" },
      { value: "8x5_9_18", label: "8x5 9h-18h" },
      { value: "8x5_8_24", label: "8x5 8h-24h" },
      { value: "na", label: "N/A" },
    ],
  },
  {
    key: "includes_replacement",
    label: "Inclui substituição de equipamentos",
    options: [
      { value: "sim", label: "Sim" },
      { value: "sim_garantia", label: "Sim (garantia do fabricante)" },
      { value: "nao", label: "Não" },
    ],
  },
];

/** The three Excel sheets (API `excel?variant=`). */
export const EXCEL_VARIANTS = [
  { value: "internal", label: "Excel W4M (interno)", hint: "Custos, margens e colunas de parceiro", internal: true },
  { value: "client", label: "Excel cliente", hint: "Serviços agregados numa linha" },
  { value: "detailed", label: "Excel detalhado", hint: "Serviços linha a linha, sem custos" },
];

/** What the supplier attachment accepts (the API refuses the rest). */
export const ATTACHMENT_ACCEPT = ".pdf,.xlsx,.xls,.csv";

export const FIELD_TYPES = [
  { value: "brand_select", label: "Marca" },
  { value: "option_select", label: "Lista de opções" },
  { value: "article_list", label: "Lista de artigos" },
  { value: "article_qty", label: "Artigo + quantidade" },
  { value: "number", label: "Número" },
  { value: "yes_no", label: "Sim / Não" },
  { value: "text", label: "Texto" },
];

/** Where a question reads its articles from. */
export const FIELD_SOURCES = [
  { value: "service", label: "Tabelas do serviço", hint: "Todas as tabelas associadas ao serviço" },
  { value: "brand", label: "Marca escolhida", hint: "O catálogo da marca da pergunta «Marca»" },
  { value: "table", label: "Uma tabela", hint: "Uma tabela comum, escolhida abaixo" },
];

/** Icons a question may carry (PrimeIcons); any emoji typed by hand also works. */
export const ICON_PALETTE = [
  "pi pi-wifi", "pi pi-sitemap", "pi pi-server", "pi pi-desktop", "pi pi-video", "pi pi-camera",
  "pi pi-phone", "pi pi-mobile", "pi pi-box", "pi pi-shopping-cart", "pi pi-wrench", "pi pi-cog",
  "pi pi-bolt", "pi pi-map-marker", "pi pi-car", "pi pi-clock", "pi pi-users", "pi pi-building",
  "pi pi-home", "pi pi-tag", "pi pi-list", "pi pi-hashtag", "pi pi-euro", "pi pi-shield",
];

/** How the quantity of a line comes from the questionnaire (the rule form composes the formula). */
export const QTY_MODES = [
  { value: "fixed", label: "Fixa", hint: "Sempre a mesma quantidade (ex. 1)" },
  { value: "multiple", label: "Por pergunta", hint: "A resposta de uma pergunta × um factor" },
  { value: "yes_no", label: "Se sim", hint: "Uma quantidade quando a pergunta sim/não é sim" },
  { value: "list", label: "Lista de artigos", hint: "Cada artigo escolhido na pergunta dá a sua linha" },
  { value: "formula", label: "Fórmula", hint: "Escreva a fórmula à mão" },
];

/** How the unit price of a line is found. */
export const PRICE_MODES = [
  { value: "margin", label: "Custo + margem", hint: "O custo do artigo com a margem da linha" },
  { value: "fixed", label: "Preço fixo", hint: "Um valor unitário escrito aqui" },
  { value: "tier", label: "Por escalões", hint: "ESCALAO(QT, …): o preço baixa com a quantidade" },
  { value: "formula", label: "Fórmula", hint: "Escreva a fórmula de preço à mão" },
];

export const BLOCKS = [
  { value: "one_time", label: "Investimento" },
  { value: "monthly", label: "Mensalidade" },
];

export const RULE_ORIGINS = [
  { value: "list", label: "Lista de artigos", hint: "Uma linha por artigo escolhido na pergunta" },
  { value: "article", label: "Artigo fixo", hint: "Sempre o mesmo artigo do catálogo" },
  { value: "field", label: "Artigo da pergunta", hint: "O artigo escolhido numa pergunta artigo + quantidade" },
  { value: "fixed", label: "Valor calculado", hint: "Custo e preço por fórmula, sem artigo" },
];

/** Tabs of the permissions screen (`/quotes?dom=permissoes&sub=`). */
export const ACCESS_ITEMS = [
  { value: "papeis", label: "Papéis", icon: "pi pi-fw pi-id-card" },
  { value: "utilizadores", label: "Utilizadores", icon: "pi pi-fw pi-users" },
  { value: "servicos", label: "Serviços", icon: "pi pi-fw pi-sitemap" },
  { value: "parceiros", label: "Parceiros", icon: "pi pi-fw pi-briefcase" },
];

/** Tabs of the administration panel (`/quotes?dom=administracao&sub=`), phase 1b. */
export const SETTINGS_ITEMS = [
  { value: "resumo", label: "Resumo", icon: "pi pi-fw pi-chart-bar" },
  { value: "servicos", label: "Tipos de serviço", icon: "pi pi-fw pi-sitemap" },
  { value: "tabelas", label: "Tabelas de produtos", icon: "pi pi-fw pi-table" },
  { value: "opcoes", label: "Listas de opções", icon: "pi pi-fw pi-list" },
  { value: "filiais", label: "Filiais", icon: "pi pi-fw pi-map-marker" },
  { value: "phc", label: "Integração PHC", icon: "pi pi-fw pi-sync" },
  { value: "assistente", label: "Assistente", icon: "pi pi-fw pi-comments" },
];

/** Outcome of sending an article to PHC (`phc_status` on the article; null = never sent). */
export const PHC_SYNC_STATES = {
  synced: { label: "Enviado", severity: "success" },
  error: { label: "Erro", severity: "danger" },
  blocked: { label: "A corrigir", severity: "warning" },
};

/** Debounce of the editor's `calculate` after typing in a number/text field. */
export const CALCULATE_DEBOUNCE_MS = 400;
